import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import { getLanguages, addLanguages, submitTeacherScreening } from '../../api/profile';
import { fetchProfile } from '../../store/slices/profileSlice';
import { logout } from '../../store/slices/authSlice';
import brandLogo from '../../assets/images/logo.png';
import './Onboarding.scss';

export const Onboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((s) => s.auth);
  const { profile } = useSelector((s) => s.profile);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Master languages list
  const [allLanguages, setAllLanguages] = useState([]);

  // Flow control
  // Current screen/step: 'screening' | 'languages'
  const [currentStep, setCurrentStep] = useState('languages');

  // Selected languages state (holds IDs)
  const [selectedLangIds, setSelectedLangIds] = useState([]);

  // Screening form state
  const [screeningForm, setScreeningForm] = useState({
    yearsOfExperience: '',
    languagesQualified: '',
    canSpeakClearly: 'Yes',
    hasReliableInternet: 'Yes',
    hasTeachingCertificate: 'No',
    comfortableWithGroupAndOneOnOne: 'Yes',
    teachingStyle: '',
    studentEngagementMethods: '',
    hasQuietWellLitBackground: 'Yes',
    profile_url: '',
  });

  const [certificateFile, setCertificateFile] = useState(null);

  const isTeacher = profile?.user_type === 'teacher';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const loadData = async () => {
      try {
        setInitialLoading(true);
        // Fetch/dispatch profile if not loaded
        let currentProfile = profile;
        if (!currentProfile) {
          const res = await dispatch(fetchProfile()).unwrap();
          currentProfile = res?.data || res?.user;
        }

        // Fetch all available languages on platform
        const langRes = await getLanguages();
        if (langRes && langRes.data) {
          setAllLanguages(langRes.data);
        }

        if (currentProfile) {
          // If teacher and screening questionnaire is not complete
          // AND the global active screening flag is enabled (isQuestionComplete would be false if screening is active and incomplete)
          if (currentProfile.user_type === 'teacher' && currentProfile.screening_status === 'pending') {
            setCurrentStep('review');
          } else if (currentProfile.user_type === 'teacher' && (currentProfile.isQuestionComplete === false || currentProfile.screening_status === 'rejected')) {
            setCurrentStep('screening');
          } else {
            setCurrentStep('languages');
          }

          // If they already have languages and are complete, redirect to home
          const hasLanguages = currentProfile.languages && currentProfile.languages.length > 0;
          const screeningPassed =
            currentProfile.user_type !== 'teacher' ||
            (currentProfile.isQuestionComplete === true && currentProfile.screening_status === 'approved');
          
          if (hasLanguages && screeningPassed && currentProfile.screening_status !== 'pending') {
            navigate('/');
          }
        }
      } catch (err) {
        console.error('Onboarding load error:', err);
        setError('Failed to retrieve onboarding configuration.');
      } finally {
        setInitialLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated, navigate, dispatch]);

  const handleLanguageToggle = (id) => {
    setSelectedLangIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCertificateFile(e.target.files[0]);
    }
  };

  const handleExitApp = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleScreeningSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('yearsOfExperience', screeningForm.yearsOfExperience);
      formData.append('languagesQualified', screeningForm.languagesQualified);
      formData.append('canSpeakClearly', screeningForm.canSpeakClearly);
      formData.append('hasReliableInternet', screeningForm.hasReliableInternet);
      formData.append('hasTeachingCertificate', screeningForm.hasTeachingCertificate);
      formData.append('comfortableWithGroupAndOneOnOne', screeningForm.comfortableWithGroupAndOneOnOne);
      formData.append('teachingStyle', screeningForm.teachingStyle);
      formData.append('studentEngagementMethods', screeningForm.studentEngagementMethods);
      formData.append('hasQuietWellLitBackground', screeningForm.hasQuietWellLitBackground);
      formData.append('profile_url', screeningForm.profile_url);

      if (certificateFile) {
        formData.append('certificateFile', certificateFile);
      }

      await submitTeacherScreening(formData);
      
      // Update profile status in Redux
      const freshProfile = await dispatch(fetchProfile()).unwrap();
      const updatedProfile = freshProfile?.data || freshProfile?.user;
      
      // Redirect to languages next step or review step
      if (updatedProfile?.user_type === 'teacher' && updatedProfile?.screening_status === 'pending') {
        setCurrentStep('review');
      } else {
        setCurrentStep('languages');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to submit screening questions.');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguagesSubmit = async (e) => {
    e.preventDefault();
    if (selectedLangIds.length === 0) {
      setError('Please select at least one language to continue.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await addLanguages(selectedLangIds);
      // Fetch updated profile
      await dispatch(fetchProfile()).unwrap();
      navigate('/');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to save languages.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="onboarding-loading-screen d-flex flex-column align-items-center justify-content-center">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <h5 className="text-gray-700 fw-bold">Setting up your onboarding...</h5>
      </div>
    );
  }

  return (
    <div className="onboarding-page-wrapper py-5">
      <Container>
        {/* Brand Header */}
        <div className="text-center mb-5">
          <img src={brandLogo} alt="Yoospeak" className="img-fluid onboarding-logo mb-3" />
          <h4 className="fw-black text-gray-900">Welcome to Yoospeak!</h4>
          <p className="text-muted">Let's finish setting up your account</p>
        </div>

        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

            {currentStep === 'screening' && (
              <Card className="onboarding-card border-0 shadow-lg p-4 p-md-5 rounded-4">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-4">
                    <span className="step-badge rounded-circle">1</span>
                    <h2 className="step-title h4 mb-0">Teacher Screening Questionnaire</h2>
                  </div>
                  <p className="text-muted text-sm mb-4">
                    Your answers will help us decide if you qualify to teach on the platform.
                  </p>

                  {profile?.screening_status === 'rejected' && (
                    <Alert variant="danger" className="rounded-3 shadow-sm mb-4">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      Your previous screening application was rejected. Please review your answers below, correct them, and resubmit.
                    </Alert>
                  )}

                  <Form onSubmit={handleScreeningSubmit}>
                    {/* Experience */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        1. Do you have previous teaching experience? If yes, how many years?
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="1"
                        min="0"
                        placeholder="Enter experience"
                        value={screeningForm.yearsOfExperience}
                        onChange={(e) => setScreeningForm({ ...screeningForm, yearsOfExperience: e.target.value })}
                        required
                      />
                    </Form.Group>

                    {/* Languages Qualified */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        2. Which language(s) are you qualified and confident to teach?
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter languages"
                        value={screeningForm.languagesQualified}
                        onChange={(e) => setScreeningForm({ ...screeningForm, languagesQualified: e.target.value })}
                        required
                      />
                    </Form.Group>

                    {/* Speak clearly */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        3. Can you speak and communicate clearly in that language?
                      </Form.Label>
                      <div className="d-flex gap-4 mt-2">
                        <Form.Check
                          type="radio"
                          id="speak-yes"
                          label="Yes"
                          name="canSpeakClearly"
                          checked={screeningForm.canSpeakClearly === 'Yes'}
                          onChange={() => setScreeningForm({ ...screeningForm, canSpeakClearly: 'Yes' })}
                        />
                        <Form.Check
                          type="radio"
                          id="speak-no"
                          label="No"
                          name="canSpeakClearly"
                          checked={screeningForm.canSpeakClearly === 'No'}
                          onChange={() => setScreeningForm({ ...screeningForm, canSpeakClearly: 'No' })}
                        />
                      </div>
                    </Form.Group>

                    {/* Internet & Gear */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        4. Do you have a reliable internet connection, camera, and microphone for teaching?
                      </Form.Label>
                      <div className="d-flex gap-4 mt-2">
                        <Form.Check
                          type="radio"
                          id="internet-yes"
                          label="Yes"
                          name="hasReliableInternet"
                          checked={screeningForm.hasReliableInternet === 'Yes'}
                          onChange={() => setScreeningForm({ ...screeningForm, hasReliableInternet: 'Yes' })}
                        />
                        <Form.Check
                          type="radio"
                          id="internet-no"
                          label="No"
                          name="hasReliableInternet"
                          checked={screeningForm.hasReliableInternet === 'No'}
                          onChange={() => setScreeningForm({ ...screeningForm, hasReliableInternet: 'No' })}
                        />
                      </div>
                    </Form.Group>

                    {/* Teaching Certificate */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        5. Do you hold any teaching certificate or diploma?
                      </Form.Label>
                      <div className="d-flex gap-4 mt-2">
                        <Form.Check
                          type="radio"
                          id="cert-yes"
                          label="Yes"
                          name="hasTeachingCertificate"
                          checked={screeningForm.hasTeachingCertificate === 'Yes'}
                          onChange={() => setScreeningForm({ ...screeningForm, hasTeachingCertificate: 'Yes' })}
                        />
                        <Form.Check
                          type="radio"
                          id="cert-no"
                          label="No"
                          name="hasTeachingCertificate"
                          checked={screeningForm.hasTeachingCertificate === 'No'}
                          onChange={() => setScreeningForm({ ...screeningForm, hasTeachingCertificate: 'No' })}
                        />
                      </div>
                    </Form.Group>

                    {/* Group & 1 on 1 */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        6. Are you comfortable teaching both group and one-on-one sessions using the app?
                      </Form.Label>
                      <div className="d-flex flex-column gap-2 mt-2">
                        <Form.Check
                          type="radio"
                          id="group-yes"
                          label="Yes"
                          name="comfortableWithGroupAndOneOnOne"
                          checked={screeningForm.comfortableWithGroupAndOneOnOne === 'Yes'}
                          onChange={() => setScreeningForm({ ...screeningForm, comfortableWithGroupAndOneOnOne: 'Yes' })}
                        />
                        <Form.Check
                          type="radio"
                          id="group-sometimes"
                          label="Sometimes"
                          name="comfortableWithGroupAndOneOnOne"
                          checked={screeningForm.comfortableWithGroupAndOneOnOne === 'Sometimes'}
                          onChange={() => setScreeningForm({ ...screeningForm, comfortableWithGroupAndOneOnOne: 'Sometimes' })}
                        />
                        <Form.Check
                          type="radio"
                          id="group-no"
                          label="No"
                          name="comfortableWithGroupAndOneOnOne"
                          checked={screeningForm.comfortableWithGroupAndOneOnOne === 'No'}
                          onChange={() => setScreeningForm({ ...screeningForm, comfortableWithGroupAndOneOnOne: 'No' })}
                        />
                      </div>
                    </Form.Group>

                    {/* Teaching Style */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        7. How would you describe your teaching style in a few words?
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter teaching style"
                        value={screeningForm.teachingStyle}
                        onChange={(e) => setScreeningForm({ ...screeningForm, teachingStyle: e.target.value })}
                        required
                      />
                    </Form.Group>

                    {/* Student Engagement */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        8. How do you keep your students engaged and motivated?
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter engagement methods"
                        value={screeningForm.studentEngagementMethods}
                        onChange={(e) => setScreeningForm({ ...screeningForm, studentEngagementMethods: e.target.value })}
                        required
                      />
                    </Form.Group>

                    {/* Quiet Background */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        9. Can you teach in a quiet, background-noise-free space while dressed professionally on camera?
                      </Form.Label>
                      <div className="d-flex flex-column gap-2 mt-2">
                        <Form.Check
                          type="radio"
                          id="noise-yes"
                          label="Yes"
                          name="hasQuietWellLitBackground"
                          checked={screeningForm.hasQuietWellLitBackground === 'Yes'}
                          onChange={() => setScreeningForm({ ...screeningForm, hasQuietWellLitBackground: 'Yes' })}
                        />
                        <Form.Check
                          type="radio"
                          id="noise-sometimes"
                          label="Sometimes"
                          name="hasQuietWellLitBackground"
                          checked={screeningForm.hasQuietWellLitBackground === 'Sometimes'}
                          onChange={() => setScreeningForm({ ...screeningForm, hasQuietWellLitBackground: 'Sometimes' })}
                        />
                        <Form.Check
                          type="radio"
                          id="noise-no"
                          label="No"
                          name="hasQuietWellLitBackground"
                          checked={screeningForm.hasQuietWellLitBackground === 'No'}
                          onChange={() => setScreeningForm({ ...screeningForm, hasQuietWellLitBackground: 'No' })}
                        />
                      </div>
                    </Form.Group>

                    {/* Resume / Certificate Upload */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        10. Upload your resume or teaching certificate (Optional):
                      </Form.Label>
                      <div className="d-flex flex-column gap-2 mt-2">
                        <input
                          type="file"
                          id="certificate-file-input"
                          className="d-none"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                        />
                        <label
                          htmlFor="certificate-file-input"
                          className="btn btn-outline-primary w-100 py-3 rounded-pill fw-bold cursor-pointer"
                        >
                          Add
                        </label>
                        {certificateFile ? (
                          <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded border">
                            <div className="d-flex align-items-center gap-2">
                              <i className="bi bi-file-earmark-check-fill text-success fs-5"></i>
                              <span className="text-sm fw-semibold text-gray-700 text-truncate" style={{ maxWidth: '200px' }}>
                                {certificateFile.name}
                              </span>
                            </div>
                            <Button
                              variant="link"
                              className="text-danger p-0 m-0"
                              onClick={() => setCertificateFile(null)}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted text-center mt-1">No file selected (Supports PDF, JPG, PNG)</span>
                        )}
                      </div>
                    </Form.Group>

                    {/* Profile Link */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold text-gray-700">
                        11. Enter profile link (e.g., Facebook, LinkedIn, Instagram, TikTok, YouTube)
                      </Form.Label>
                      <Form.Control
                        type="url"
                        placeholder="Paste your profile link"
                        value={screeningForm.profile_url}
                        onChange={(e) => setScreeningForm({ ...screeningForm, profile_url: e.target.value })}
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="purple"
                      className="w-100 py-3 rounded-pill fw-bold text-white shadow-sm mt-3"
                      disabled={loading}
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : 'Submit'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            )}


            {currentStep === 'review' && (
              <Card className="onboarding-card border-0 shadow-lg p-4 p-md-5 rounded-4 text-center">
                <Card.Body>
                  <div className="text-center mb-4">
                    <div className="icon-pulse bg-warning bg-opacity-10 text-warning rounded-circle d-inline-flex p-4 mb-4">
                      <i className="bi bi-hourglass-split" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h2 className="step-title h4 mb-3 fw-bold text-gray-900">Account Under Review</h2>
                    <p className="text-muted text-sm mb-4">
                      Your account has not been approved yet. <br />
                      Please wait while we verify your details.
                    </p>
                    <p className="mb-4 text-sm text-gray-800">
                      Please contact <a href="mailto:support@yoospeak.app" className="text-decoration-underline fw-semibold text-primary">support@yoospeak.app</a> for approval.
                    </p>
                  </div>

                  <Button
                    variant="purple"
                    className="w-100 py-3 rounded-pill fw-bold text-white shadow-sm mb-3"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        const freshProfile = await dispatch(fetchProfile()).unwrap();
                        const updatedProfile = freshProfile?.data || freshProfile?.user;
                        if (updatedProfile) {
                          if (updatedProfile.screening_status === 'approved' || updatedProfile.isQuestionComplete) {
                            setCurrentStep('languages');
                          } else if (updatedProfile.screening_status === 'rejected') {
                            setCurrentStep('screening');
                          }
                        }
                      } catch (err) {
                        console.error(err);
                        setError('Failed to refresh status.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
                  </Button>

                  <Button
                    variant="secondary"
                    className="w-100 py-3 rounded-pill fw-bold text-white shadow-sm"
                    onClick={handleExitApp}
                  >
                    Exit App
                  </Button>
                </Card.Body>
              </Card>
            )}

            {currentStep === 'languages' && (
              <Card className="onboarding-card border-0 shadow-lg p-4 p-md-5 rounded-4">
                <Card.Body>
                  <div className="text-center mb-4">
                    <div className="lang-icon-circle mx-auto mb-3">
                      <i className="bi bi-translate text-purple fs-3"></i>
                    </div>
                    <h2 className="step-title h4 mb-2">
                      {isTeacher ? 'What language do you want to teach?' : 'What language do you want to learn?'}
                    </h2>
                    <p className="text-muted text-sm">
                      Select minimum one language to continue
                    </p>
                  </div>

                  <Form onSubmit={handleLanguagesSubmit}>
                    <div className="language-list-container mb-4">
                      {allLanguages.map((lang) => {
                        const isChecked = selectedLangIds.includes(lang._id);
                        return (
                          <div
                            key={lang._id}
                            className={`language-selection-item d-flex align-items-center justify-content-between p-3 mb-2 rounded-3 border ${
                              isChecked ? 'selected' : ''
                            }`}
                            onClick={() => handleLanguageToggle(lang._id)}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <Form.Check
                                type="checkbox"
                                id={`lang-${lang._id}`}
                                checked={isChecked}
                                onChange={() => {}} // handled by div click
                                onClick={(e) => e.stopPropagation()} // avoid double trigger
                                className="custom-check-box"
                              />
                              <div className="lang-info-block">
                                <span className="lang-name fw-semibold text-gray-900">{lang.name}</span>
                                <span className="lang-desc text-xs text-gray-500 d-block mt-0.5">
                                  {lang.teachers} Teachers available
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      type="submit"
                      variant="purple"
                      className="w-100 py-3 rounded-pill fw-bold text-white shadow-sm"
                      disabled={loading || selectedLangIds.length === 0}
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : 'Go To Dashboard'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};
